import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Plus, Edit3 } from 'lucide-react';

interface SmartGridCalendarViewProps {
  data: any[][];
  onCellChange: (row: number, col: number, value: string) => void;
  demoMode?: boolean;
}

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end?: Date;
  row: number;
  col: number;
  color: string;
}

export const SmartGridCalendarView: React.FC<SmartGridCalendarViewProps> = ({ data, onCellChange, demoMode = false }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [isEditing, setIsEditing] = useState(false);


  const getEventColor = (row: number, col: number) => {
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', 'var(--tyler-blue)', '#06b6d4'];
    return colors[(row + col) % colors.length];
  };

  const events = useMemo(() => {
    const extractedEvents: CalendarEvent[] = [];
    
    let processedData: any[][] = [];
    
    if (!data || !Array.isArray(data)) {
      return extractedEvents;
    }
    
    if (data.length > 0 && typeof data[0] === 'object' && !Array.isArray(data[0])) {
      const headers = data.length > 0 ? Object.keys(data[0]) : [];
      processedData = [headers, ...data.map(obj => headers.map(key => obj[key] || ''))];
    } else if (data.length > 0 && Array.isArray(data[0])) {
      processedData = data;
    } else {
      processedData = data.map(item => Array.isArray(item) ? item : [item]);
    }
    
    processedData.forEach((row, rowIndex) => {
      if (!Array.isArray(row)) {
        const cellValue = String(row || '');
        if (cellValue.trim()) {
          const dateMatch = cellValue.match(/(\d{4}-\d{2}-\d{2})|(\d{1,2}\/\d{1,2}\/\d{4})/);
          if (dateMatch) {
            const eventDate = new Date(dateMatch[0]);
            if (!isNaN(eventDate.getTime())) {
              extractedEvents.push({
                id: `${rowIndex}-0`,
                title: cellValue.replace(dateMatch[0], '').trim() || `Event ${rowIndex}`,
                start: eventDate,
                row: rowIndex,
                col: 0,
                color: getEventColor(rowIndex, 0)
              });
            }
          } else if (cellValue.toLowerCase().includes('task') || cellValue.toLowerCase().includes('project') || cellValue.toLowerCase().includes('meeting')) {
            extractedEvents.push({
              id: `${rowIndex}-0`,
              title: cellValue,
              start: new Date(),
              row: rowIndex,
              col: 0,
              color: getEventColor(rowIndex, 0)
            });
          }
        }
        return;
      }
      
      row.forEach((cell, colIndex) => {
        if (typeof cell === 'string' && cell.trim()) {
          const dateMatch = cell.match(/(\d{4}-\d{2}-\d{2})|(\d{1,2}\/\d{1,2}\/\d{4})/);
          if (dateMatch) {
            const eventDate = new Date(dateMatch[0]);
            if (!isNaN(eventDate.getTime())) {
              extractedEvents.push({
                id: `${rowIndex}-${colIndex}`,
                title: cell.replace(dateMatch[0], '').trim() || `Event ${rowIndex}-${colIndex}`,
                start: eventDate,
                row: rowIndex,
                col: colIndex,
                color: getEventColor(rowIndex, colIndex)
              });
            }
          } else if (cell.toLowerCase().includes('task') || cell.toLowerCase().includes('project') || cell.toLowerCase().includes('meeting')) {
            extractedEvents.push({
              id: `${rowIndex}-${colIndex}`,
              title: cell,
              start: new Date(),
              row: rowIndex,
              col: colIndex,
              color: getEventColor(rowIndex, colIndex)
            });
          }
        }
      });
    });
    
    return extractedEvents;
  }, [data]);

  const getDaysInMonth = (date: Date): (Date | null)[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days: (Date | null)[] = [];
    
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getEventsForDay = (date: Date | null) => {
    if (!date) return [];
    return events.filter(event => {
      const eventDate = new Date(event.start);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + (direction === 'next' ? 1 : -1));
      return newDate;
    });
  };

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setIsEditing(true);
  };

  const handleEventUpdate = (newTitle: string, newDate: string) => {
    if (selectedEvent) {
      const updatedValue = newDate ? `${newDate} ${newTitle}` : newTitle;
      onCellChange(selectedEvent.row, selectedEvent.col, updatedValue);
      setIsEditing(false);
      setSelectedEvent(null);
    }
  };

  const handleAddEvent = (date: Date) => {
    const title = prompt('Enter event title:');
    if (title) {
      let emptyRow = -1, emptyCol = -1;
      
      if (Array.isArray(data)) {
        for (let row = 0; row < data.length; row++) {
          if (Array.isArray(data[row])) {
            for (let col = 0; col < data[row].length; col++) {
              if (!data[row][col] || data[row][col].toString().trim() === '') {
                emptyRow = row;
                emptyCol = col;
                break;
              }
            }
          } else {
            if (!data[row] || data[row].toString().trim() === '') {
              emptyRow = row;
              emptyCol = 0;
              break;
            }
          }
          if (emptyRow !== -1) break;
        }
      }
      
      if (emptyRow !== -1) {
        const dateStr = date.toISOString().split('T')[0];
        onCellChange(emptyRow, emptyCol, `${dateStr} ${title}`);
      }
    }
  };

  const days = getDaysInMonth(currentDate);
  const monthYear = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="h-full bg-gray-900 text-white">
      {/* Calendar Header */}
      <div className="flex justify-between items-center p-4 border-b border-gray-700">
        <h2 className="text-xl font-bold">SmartGrid Calendar View</h2>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigateMonth('prev')}
              className="p-2 bg-gray-700 hover:bg-gray-600 rounded text-white"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-white font-medium min-w-48 text-center">{monthYear}</span>
            <button
              onClick={() => navigateMonth('next')}
              className="p-2 bg-gray-700 hover:bg-gray-600 rounded text-white"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="text-sm text-gray-400">
            {events.length} events from SmartGrid
            {demoMode && <span className="ml-2 text-yellow-400">(Demo Mode)</span>}
          </div>
        </div>
      </div>

      <div className="p-4 h-full">
        {/* Calendar Header Days */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="p-2 text-center text-gray-400 font-medium">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1 h-full">
          {days.map((day, index) => {
            const dayEvents = getEventsForDay(day);
            const isToday = day && day.toDateString() === new Date().toDateString();
            
            return (
              <div
                key={index}
                className={`min-h-24 p-1 border border-gray-700 rounded relative ${
                  day ? 'bg-gray-800 hover:bg-gray-750' : 'bg-gray-900'
                } ${isToday ? 'ring-2 ring-blue-500' : ''}`}
              >
                {day && (
                  <>
                    <div className={`text-sm font-medium mb-1 flex justify-between items-center ${
                      isToday ? 'text-blue-400' : 'text-slate-300'
                    }`}>
                      <span>{day.getDate()}</span>
                      <button
                        onClick={() => handleAddEvent(day)}
                        className="opacity-0 group-hover:opacity-100 hover:bg-gray-600 p-1 rounded"
                        title="Add event"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="space-y-1">
                      {dayEvents.slice(0, 3).map(event => (
                        <div
                          key={event.id}
                          onClick={() => handleEventClick(event)}
                          className="text-xs p-1 rounded cursor-pointer hover:opacity-80 truncate flex items-center gap-1"
                          style={{ backgroundColor: event.color }}
                          title={`${event.title} (Row ${event.row}, Col ${event.col})`}
                        >
                          <Edit3 className="w-2 h-2 flex-shrink-0" />
                          {event.title}
                        </div>
                      ))}
                      {dayEvents.length > 3 && (
                        <div className="text-xs text-gray-400">
                          +{dayEvents.length - 3} more
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Edit Event Modal */}
      {isEditing && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg w-96">
            <h3 className="text-lg font-bold mb-4">Edit Event</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title</label>
                <input
                  type="text"
                  defaultValue={selectedEvent.title}
                  className="w-full p-2 bg-gray-700 border border-gray-600 rounded"
                  id="event-title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Date</label>
                <input
                  type="date"
                  defaultValue={selectedEvent.start.toISOString().split('T')[0]}
                  className="w-full p-2 bg-gray-700 border border-gray-600 rounded"
                  id="event-date"
                />
              </div>
              <div className="text-sm text-gray-400">
                Grid Position: Row {selectedEvent.row + 1}, Column {selectedEvent.col + 1}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const title = (document.getElementById('event-title') as HTMLInputElement).value;
                    const date = (document.getElementById('event-date') as HTMLInputElement).value;
                    handleEventUpdate(title, date);
                  }}
                  className="flex-1 bg-[#4267A1]/60 hover:bg-[#4267A1]/70 text-white py-2 px-4 rounded"
                >
                  Update
                </button>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setSelectedEvent(null);
                  }}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
